/**
 * Typed wrappers for the citizen parcel-booking endpoints on service-mail.
 *
 * Endpoints used:
 *   POST  {mailBase}/v1/parcels-mine         — book a parcel (F-383).
 *   GET   {mailBase}/v1/parcels-mine/quote   — compute quote (dry-run).
 *
 * RBAC: both endpoints require the citizen JWT to carry `Parcel/create-own`
 * and `Parcel/read-own` respectively. These are seeded to the `customer` role
 * by `20260507_customer_role.sql` so every registered citizen has them.
 *
 * Payment: only NIPOST_PAY is accepted in this slice. The citizen must have
 * an activated NIPOST Pay wallet (POST /v1/wallet/wallets-mine) with sufficient
 * balance to cover the booking amount.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ServiceClass = 'STANDARD' | 'EXPRESS' | 'EMS' | 'REGISTERED_MAIL';

export interface BookingParty {
  readonly name: string;
  readonly phone: string;
  readonly email?: string;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  readonly postalCode?: string;
  // service-geo soft-FK fields. All optional — legacy code paths don't set
  // them, and non-Nigerian addresses don't have LGA / ward.
  readonly geoCountryId?: number;
  readonly geoCountryName?: string;
  readonly geoStateId?: number;
  readonly geoStateName?: string;
  readonly geoLgaId?: number;
  readonly geoLgaName?: string;
  readonly geoWardId?: number;
  readonly geoWardName?: string;
}

export interface BookingPackage {
  readonly weightKg: number;
  readonly lengthCm: number;
  readonly widthCm: number;
  readonly heightCm: number;
  readonly contentDescription: string;
  readonly declaredValueNgn?: string;
}

export interface CreateParcelBookingRequest {
  readonly sender: BookingParty;
  readonly receiver: BookingParty;
  readonly package: BookingPackage;
  readonly serviceClass: ServiceClass;
  readonly paymentMethod: 'NIPOST_PAY' | 'CARD';
  /** Required when `paymentMethod === 'CARD'` — the Paystack reference
   *  returned by service-payment's verify endpoint. The booking flow attaches
   *  it to the mail item's pricing JSON; no wallet debit happens on this path. */
  readonly cardPaymentReference?: string;
  readonly insuranceSelected?: boolean;
}

export interface ParcelBookingResponse {
  readonly trackingNumber: string;
  readonly mailItemId: string;
  readonly amountNgn: string;
  readonly status: 'PENDING_DROPOFF';
  readonly dropoffInstructions: string;
  readonly quoteExpiresAt: string;
}

export interface ParcelQuoteResponse {
  readonly amountNgn: string;
  readonly tariffId: string;
  readonly quoteExpiresAt: string;
  readonly note: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Book a parcel from home. Debits the citizen's NIPOST Pay wallet.
 * Requires a valid citizen JWT with Parcel/create-own permission.
 */
export async function bookParcel(
  req: CreateParcelBookingRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<ParcelBookingResponse> {
  return requestJson<ParcelBookingResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: '/v1/parcels-mine',
    body: req,
    bearerToken: getCustomerToken() ?? undefined,
    ...options,
  });
}

/**
 * Compute a price quote without making a booking or debiting the wallet.
 * Uses query parameters for a cheap GET request.
 */
export async function getParcelQuote(
  senderState: string,
  receiverState: string,
  serviceClass: ServiceClass,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<ParcelQuoteResponse> {
  const qs = new URLSearchParams({
    senderState,
    receiverState,
    serviceClass,
  });
  return requestJson<ParcelQuoteResponse>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/parcels-mine/quote?${qs.toString()}`,
    bearerToken: getCustomerToken() ?? undefined,
    ...options,
  });
}
